import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { DataMainTemplate } from "@nocios/presentation-kit";
import Config from "src/Constants/Config";
import setCurrentPage from "src/Utils/setCurrentPage";

// Constants
const PAGE_ID = 1;
const BG_ID = 1;
const BG_TYPE = "jpg";
const ANIMATION_DELAY = 150; // Delay between each button animation
const ANIMATION_DURATION = 800; // Duration of each button animation

// Target pages for each button
// Button 1 now doesn't lead to any page (null)
const TARGET_PAGES: (number | null)[] = [null, 2, 3, 4, 5, 6];

// Button configurations
interface ButtonConfig {
  id: number;
  label: string;
  imageSrc: string;
  targetPage: number | null;
  onClick?: () => void;
}

// Component for each clickable button
interface StoryButtonProps {
  config: ButtonConfig;
  onClick: (pageId: number | null) => void;
  index: number;
  animationComplete: boolean;
  currentPage: number; // Add currentPage prop
}

const StoryButton: React.FC<StoryButtonProps> = ({
  config,
  onClick,
  index,
  animationComplete,
  currentPage, // Destructure the new prop
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (currentPage === PAGE_ID) {
      // Reset to invisible when page becomes active
      setVisible(false);
      const timer = setTimeout(() => {
        setVisible(true); // Trigger animation after delay
      }, index * ANIMATION_DELAY);
      return () => clearTimeout(timer);
    } else {
      // Ensure buttons are hidden when page is not active
      setVisible(false);
    }
  }, [currentPage, index]); // Add currentPage to dependencies

  return (
    <div
      className="story-button"
      onClick={() => animationComplete && onClick(config.targetPage)}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "var(--width-225)",
        cursor: animationComplete && config.targetPage !== null ? "pointer" : "default",
        transition: visible ? `all 0.3s ease, transform ${ANIMATION_DURATION}ms cubic-bezier(0.34, 1.56, 0.64, 1)` : "none",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(100px)",
        pointerEvents: animationComplete ? "auto" : "none",
      }}
    >
      <div className="button-container" style={{ width: "var(--width-225)", height: "var(--height-225)" }}>
        <img
          src={config.imageSrc}
          alt={config.label}
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            transition: "all 0.3s ease",
            objectFit: "cover",
          }}
          className="button-image"
        />
      </div>
      <div
        style={{
          marginTop: "var(--height-10)",
          fontSize: "var(--width-16)",
          color: "#fff",
          textAlign: "center",
          fontWeight: "bold",
        }}
      >
        {config.label}
      </div>
    </div>
  );
};

// Main component that renders the grid of buttons
const ButtonGrid: React.FC<{
  onButtonClick: (pageId: number | null) => void;
}> = ({ onButtonClick }) => {
  const baseImageUrl = `${Config.BASE_URL}/images/screen_${PAGE_ID}/`;
  const [animationComplete, setAnimationComplete] = useState(false);
  // Get the current page from your Redux store
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  useEffect(() => {
    // When currentPage is equal to this page's PAGE_ID, reset the animation state
    if (currentPage === PAGE_ID) {
      setAnimationComplete(false);
      const totalAnimationTime = 6 * ANIMATION_DELAY + ANIMATION_DURATION - 800;
      const timer = setTimeout(() => {
        setAnimationComplete(true);
      }, totalAnimationTime);
      return () => clearTimeout(timer);
    }
  }, [currentPage]);

  // Create button configurations
  const buttons: ButtonConfig[] = [
    {
      id: 1,
      label: "",
      imageSrc: `${baseImageUrl}button_1.png`,
      targetPage: null,
      onClick: () => (window.location.href = Config.ILO),
    },
    {
      id: 2,
      label: "",
      imageSrc: `${baseImageUrl}button_2.png`,
      targetPage: TARGET_PAGES[1],
    },
    {
      id: 3,
      label: "",
      imageSrc: `${baseImageUrl}button_3.png`,
      targetPage: TARGET_PAGES[2],
    },
    {
      id: 4,
      label: "",
      imageSrc: `${baseImageUrl}button_4.png`,
      targetPage: TARGET_PAGES[3],
    },
    {
      id: 5,
      label: "",
      imageSrc: `${baseImageUrl}button_5.png`,
      targetPage: TARGET_PAGES[4],
    },
    {
      id: 6,
      label: "",
      imageSrc: `${baseImageUrl}button_6.png`,
      targetPage: TARGET_PAGES[5],
    },
  ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "var(--height-20) var(--width-140)",
        justifyContent: "center",
        justifyItems: "center",
        width: "var(--width-900)",
        margin: "0 auto",
      }}
    >
      {buttons.map((button, index) => (
        <StoryButton
          key={button.id}
          config={button}
          onClick={
            button.onClick
              ? () => {
                  console.log("Button clicked", button);
                  if (button.onClick) button.onClick();
                }
              : onButtonClick
          }
          index={index}
          animationComplete={animationComplete}
          currentPage={currentPage}
        />
      ))}
    </div>
  );
};

// Main data function that returns the template data
const DATA = (): DataMainTemplate => {
  const baseImageBG = `${Config.BASE_URL}/images/BG/`;

  // Navigation handler
  const handleButtonClick = (pageId: number | null) => {
    if (pageId !== null) {
      setCurrentPage(pageId);
    }
  };

  return {
    bgVideo: {
      poster: `${baseImageBG}bg_${BG_ID}.${BG_TYPE}`,
      video: `${Config.BASE_URL}/bg_${BG_ID}.mp4`,
    },
    gradient:
      "linear-gradient(270deg, rgba(21, 50, 121, 0.00) -16.3%, rgba(21, 50, 121, 0.80) 100.68%), linear-gradient(0deg, rgba(0, 0, 0, 0.10) 0%, rgba(0, 0, 0, 0.10) 100%)",
    styleBody: { paddingBottom: 0 },
    title: "CSP Best Stories", // Empty title - we'll add our own in the component
    description: "", // Empty description - we'll add our own in the component
    styleTitle: { opacity: 0 }, // Hide default title
    styleDescription: { opacity: 0 }, // Hide default description
    rightSideComponent: (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {/* Custom centered title and subtitle using existing fonts */}
        <div
          style={{
            width: "100%",
            textAlign: "center",
            marginBottom: "var(--height-30)",
          }}
        >
          <h1
            style={{
              fontSize: "var(--width-78)",
              fontFamily: "Tungsten",
              fontWeight: "100",
              color: "#ffffff",
              marginBottom: "10px",
              textTransform: "uppercase",
              lineHeight: "110%",
            }}
          >
            CSP Best Stories
          </h1>
          <h2
            style={{
              fontSize: "var(--width-18)",
              fontFamily: "Myriad Pro",
              color: "#ffffff",
              fontWeight: "700",
              lineHeight: "140%",
            }}
          >
            Select one of the stories below to start
          </h2>
        </div>

        {/* Grid of buttons */}
        <ButtonGrid onButtonClick={handleButtonClick} />
      </div>
    ),
  };
};

// Add global styles for the hover effects
const globalStyles = `
  .button-container {
    border-radius: 50%;
    overflow: hidden;
    box-shadow: 0 0 15px rgba(0, 100, 255, 0.5);
    transition: all 0.3s ease;
  }
  
  .story-button:hover {
    transform: translateY(-5px) !important;
  }
  
  .story-button:hover .button-container {
    box-shadow: 0 0 30px 5px rgba(50, 150, 255, 0.9);
  }
`;

// Add the styles to the document
const styleElement = document.createElement("style");
styleElement.innerHTML = globalStyles;
document.head.appendChild(styleElement);

export default DATA;
